import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {
  arrayPatternValidator
} from '@services/administration/machine/trigger/validators/array-pattern-validator.directive.service';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {TriggerQueryAction} from '@models/adminstration/machine/trigger/trigger-query/trigger-query-action';
import {User} from '@models/user/user';
import {EMAIL_REGEX} from '@constants/reg-exp';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {Role} from '@models/user/role';
import {Subject} from 'rxjs';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {switchMap, takeUntil} from 'rxjs/operators';

type ChangeCallbackFn<T> = (value: T) => void;
type TouchCallbackFn = () => void;

@Component({
    selector: 'scitis-admin-notification-action-type',
    templateUrl: './notification-action-type.component.html',
    styleUrls: ['./notification-action-type.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NotificationActionTypeComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NotificationActionTypeComponent),
            multi: true
        }
    ],
    standalone: false
})
export class NotificationActionTypeComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  triggerQueryAction: TriggerQueryAction = new TriggerQueryAction();

  @Output()
  updatedTriggerQueryAction: EventEmitter<TriggerQueryAction> = new EventEmitter<TriggerQueryAction>();

  @Output()
  formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  superOrTechAdminRole = false;
  isDistributorOrHigher = false;
  isLoadingSubscribers: boolean = false;
  userNames: string[] = new Array<string>();
  subscriberList: string[] = new Array<string>();
  loggedUser: User = new User();
  triggerQueryNotificationForm: UntypedFormGroup = this.fb.group({});
  usernameSubscribed = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private CURRENT_PAGE_NAME: string = 'NotificationActionTypeComponent';

  constructor(
      private fb: UntypedFormBuilder,
      private elasticSearchService: ElasticSearchService,
      private translate: TranslateService,
      private notificationService: NotificationService,
      private sortingService: SortingService,
      private machineService: MachineService,
      private firebaseAuthenticationService: FirebaseAuthenticationService) {
  }

  // convenience getter for easy access to form fields
  get formControls() {
    return this.triggerQueryNotificationForm.controls;
  }

  ngOnInit(): void {
    this.setSuperOrTechAdminRole();
    this.setDistributorRoleOrHigherFlag();
    this.setUserInfoAndUserNames();
    this.generateTriggerQueryNotificationForm();
    if (!this.isDistributorOrHigher) {
      this.subscribeLoggedUser();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.triggerQueryNotificationForm.invalid) {
      return {invalidForm: {valid: false, message: 'NotificationActionTypeComponent fields are invalid'}};
    }
    return null;
  }

  writeValue(val: any): void {
    if (val) {
      this.triggerQueryNotificationForm.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: ChangeCallbackFn<object>): void {
    this.triggerQueryNotificationForm.valueChanges.subscribe(fn);

  }

  registerOnTouched(fn: TouchCallbackFn): void {
    // needed to implement ControlValueAccessor interface
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.triggerQueryNotificationForm.disable();
    } else {
      this.triggerQueryNotificationForm.enable();
    }
  }

  onFocusOut(): void {
    this.onTriggerQueryActionUpdated();
  }

  onNewSubscriberChange(subscriber: string): void {
    this.triggerQueryNotificationForm.controls.newSubscriber.setValue('');
    if (!this.machineService.isNullOrEmptyOrWhiteSpace(subscriber) && this.subscriberList.indexOf(subscriber) < 0) {
      this.subscriberList.push(subscriber);
    }
    this.userNames = this.userNames.filter(userName => userName !== subscriber);
    this.userNames = [...this.userNames].sort((a, b) => this.sortingService.sortStrings(a, b));

    this.triggerQueryNotificationForm.controls.subscriberList.setValue(this.subscriberList);
    this.triggerQueryNotificationForm.controls.subscriberList.updateValueAndValidity();
    this.onFocusOut();
  }

  onRemoveSubscriber(subscriber: string): void {
    this.subscriberList = [...this.subscriberList.filter(item => item !== subscriber)];
    if (!this.userNames.includes(subscriber)) {
      this.userNames.push(subscriber);
      this.userNames = [...this.userNames].sort((a, b) => this.sortingService.sortStrings(a, b));
    }
    this.triggerQueryNotificationForm.controls.subscriberList.setValue(this.subscriberList);
    this.triggerQueryNotificationForm.controls.subscriberList.updateValueAndValidity();
    if (!this.isDistributorOrHigher) {
      if (subscriber === this.loggedUser.username) {
        this.usernameSubscribed = false;
      }
    }
    this.onFocusOut();
  }

  subscribeLoggedUser(): void {
    if (!this.subscriberList.includes(this.loggedUser.username)) {
      this.userNames.push(this.loggedUser.username);
      this.userNames = [...this.userNames].sort((a, b) => this.sortingService.sortStrings(a, b));
      this.subscriberList.push(this.loggedUser.username);
      this.isLoadingSubscribers = false;
      this.triggerQueryNotificationForm.controls.subscriberList.setValue(this.subscriberList);
      this.usernameSubscribed = true;
      this.onFocusOut();
    }
  }

  showRemoveSubscriptionButton(subscriber: string): boolean {
    let displayButton = true;
    if (!this.isDistributorOrHigher) {
      this.triggerQueryAction.subscriberList.forEach((email: string) => {
        if (subscriber !== this.loggedUser.username && email === subscriber) {
          displayButton = false;
        }
      });
    }
    return displayButton;
  }

  private updateSubscriberList(): void {
    const updatedSubscriberList = [...this.subscriberList];
    this.triggerQueryNotificationForm.controls.subscriberList.setValue([...updatedSubscriberList]);
    this.onFocusOut();
  }

  private onTriggerQueryActionUpdated() {
    const triggerQueryActionUpdated = new TriggerQueryAction();
    triggerQueryActionUpdated.newSubscriber = this.formControls.newSubscriber.value;

    triggerQueryActionUpdated.subscriberList = [...this.formControls.subscriberList.value];
    this.formControls.subscriberList.updateValueAndValidity();

    triggerQueryActionUpdated.subject = this.formControls.subject.value;
    triggerQueryActionUpdated.message = this.formControls.message.value;

    this.updatedTriggerQueryAction.emit(triggerQueryActionUpdated);
    this.formIsValid.emit(!this.triggerQueryNotificationForm.invalid);
  }

  private setSuperOrTechAdminRole(): void {
    this.superOrTechAdminRole = this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole;
  }

  private setDistributorRoleOrHigherFlag(): void {
    this.isDistributorOrHigher = this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole || this.userRole.isAdminRole || this.userRole.isDistributorRole;
  }

  private setUserInfoAndUserNames(): void {
    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(
      switchMap((result: User) => this.elasticSearchService.listAllAccessibleUsers(result)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (success: User[]) => {
        if (success) {
          let userNames: string[] = [];
          success.forEach((user: User) => {
            if (!user.banned) {
              userNames.push(user.username);
            }
          });
          userNames = userNames.sort((a, b) => this.sortingService.sortStrings(a, b));
          this.userNames = [...userNames];
          this.isLoadingSubscribers = false;
          this.setSubscriberList();
        }
      },
      error: (error) => {
        console.log(error);
        this.isLoadingSubscribers = false;
        const message = this.translate.instant('Error getting users list.');
        this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }


  private generateTriggerQueryNotificationForm(): void {
    this.triggerQueryNotificationForm = this.fb.group({
      newSubscriber: [''],
      subscriberList: [[...this.triggerQueryAction.subscriberList], [Validators.required, arrayPatternValidator(EMAIL_REGEX)]],
      subject: [this.triggerQueryAction.subject, Validators.required],
      message: [this.triggerQueryAction.message, Validators.required]
    });

    this.formIsValid.emit(!this.triggerQueryNotificationForm.invalid);
  }

  private setSubscriberList() {
    if (this.isDistributorOrHigher) {
      this.fillEmailAddressesNotUsersAndFillSubscriberList();
    }
  }

  private fillEmailAddressesNotUsersAndFillSubscriberList() {
    this.triggerQueryAction.subscriberList.forEach(tmp => {
      if (this.userNames.includes(tmp)) {
        this.subscriberList.push(tmp);
      } else {
        if (this.superOrTechAdminRole) {
          this.subscriberList.push(tmp);
        }
      }
      if (tmp === this.loggedUser.username) {
        this.usernameSubscribed = true;
      }
      this.updateSubscriberList();
    });
  }
}
